// ============================================================================
// Canonical Sheep Dailys route contracts — the hub AND its daily record
// ============================================================================
//
// SHEEP_ROUTES gives SheepDailysRouter the whole `/sheep/dailys/*` space, and
// the router treats every non-empty suffix as a record id:
//
//   /sheep/dailys        -> sheep.dailys.index   (SheepDailysHub)
//   /sheep/dailys/       -> sheep.dailys.index   (empty suffix, hub again)
//   /sheep/dailys/<id>   -> sheep.dailys.detail  (SheepDailyPage)
//
// This module was `sheepDailysDetailPath.ts` and held only the record half,
// because that half shipped alone: the router WAS an asymmetric pair, released
// at its record page while its hub still came off the global boot read. The
// index release ended that asymmetry, so the file was renamed and the pair now
// lives together, as `layerDailysPaths.ts` argues it must — the router's own
// expression is `pathname.startsWith('/sheep/dailys/') ? pathname.slice(len) ||
// null : null`, so the index predicate's correctness is a statement about what
// the record predicate does NOT claim, and vice versa. Splitting them across
// two modules would mean writing an exclusion in one file that only the other
// file's contents make true.
//
// Neither predicate knows about the ledger, the renderer, or any data owner.
//
// RESERVED SEGMENTS: there are NONE beneath `/sheep/dailys/`. SHEEP_ROUTES
// declares no sibling namespace under it (unlike `/cattle/batches/forecast`),
// src/lib/routes.ts registers exactly one `/sheep/dailys` path
// (`VIEW_TO_PATH.sheepdailys`) and has no exact or prefix alias inside it, and
// the router's own id expression is a bare `slice` with no special-cased value.
// A future reserved segment must be added here AND to the static proof that
// pins those three facts; nothing else in this module would notice it.
// ============================================================================

import {isCanonicalCommentAnchorHash} from './commentAnchorHash.ts';

export const SHEEP_DAILYS_INDEX_PATHNAME = '/sheep/dailys';
export const SHEEP_DAILYS_DETAIL_PATHNAME_PREFIX = '/sheep/dailys/';

type LocationLike = Readonly<{pathname?: unknown; search?: unknown; hash?: unknown}>;

function pathnameOf(location: unknown): unknown {
  if (typeof location === 'string') return location;
  if (!location || typeof location !== 'object' || Array.isArray(location)) return null;
  return (location as LocationLike).pathname;
}

/**
 * The bare hub path, and nothing else.
 *
 * `/sheep/dailys/` is excluded deliberately: SheepDailysRouter DOES render the
 * hub there (the suffix is empty, so `dailyId` is null), but the splat base is
 * not the audited direct-entry route, and the generic resolver classifies it as
 * this same index surface. Leaving it inside the release would hand a RELEASED
 * verdict to a raw shape no reviewer admitted; the disposition boundary
 * instead supplies explicit recovery.
 */
export function isCanonicalSheepDailysIndexPathname(pathname: unknown): pathname is string {
  return pathname === SHEEP_DAILYS_INDEX_PATHNAME;
}

/**
 * Accept exactly one meaningful raw record-id segment. The raw form is checked
 * first so a literal separator, NUL, or line break cannot reach the decoder,
 * then the value is decoded ONCE and re-checked so an encoded separator, dot
 * segment, NUL, or line break cannot widen this released record capability.
 * A malformed escape throws inside decodeURIComponent and is refused.
 */
export function isCanonicalSheepDailysDetailPathname(pathname: unknown): pathname is string {
  if (typeof pathname !== 'string' || !pathname.startsWith(SHEEP_DAILYS_DETAIL_PATHNAME_PREFIX)) {
    return false;
  }

  const rawId = pathname.slice(SHEEP_DAILYS_DETAIL_PATHNAME_PREFIX.length);
  if (
    rawId === '' ||
    rawId.includes('/') ||
    rawId.includes('\\') ||
    rawId.includes('\0') ||
    rawId.includes('\r') ||
    rawId.includes('\n') ||
    rawId.includes('?') ||
    rawId.includes('#')
  ) {
    return false;
  }

  try {
    const id = decodeURIComponent(rawId);
    return (
      id.trim() !== '' &&
      id !== '.' &&
      id !== '..' &&
      !id.includes('/') &&
      !id.includes('\\') &&
      !id.includes('\0') &&
      !id.includes('\r') &&
      !id.includes('\n') &&
      !id.includes('?') &&
      !id.includes('#')
    );
  } catch {
    return false;
  }
}

/**
 * The hub interprets NEITHER search nor hash. `SheepDailysHub` takes no props
 * and calls no location hook at all — the router reads `location.pathname` and
 * mounts the hub bare — and its filter, flock and saved-view state is component
 * or persisted state, never URL state. So a presentation-only query or fragment
 * on the canonical hub path does not change this claim, exactly as on the
 * released Cattle and Layer Dailys indexes.
 */
export function isCanonicalSheepDailysIndexLocation(location: unknown): boolean {
  return isCanonicalSheepDailysIndexPathname(pathnameOf(location));
}

/**
 * React Router supplies pathname/search/hash separately. SheepDailyPage READS
 * `location.hash` to scroll a deep-linked anchor into view, so a hash-carrying
 * load is a materially different render than the audited one; keep both search
 * and hash out of the released pair and route them through explicit recovery.
 * A bare string is accepted only when it is plainly a pathname, so a test seam
 * or caller cannot smuggle a query or fragment into the released target.
 */
export function isCanonicalSheepDailysDetailLocation(location: unknown): boolean {
  if (typeof location === 'string') return isCanonicalSheepDailysDetailPathname(location);
  if (!location || typeof location !== 'object' || Array.isArray(location)) return false;

  const {pathname, search, hash} = location as LocationLike;
  return (
    (search === undefined || search === '') &&
    (hash === undefined || hash === '' || isCanonicalCommentAnchorHash(hash)) &&
    isCanonicalSheepDailysDetailPathname(pathname)
  );
}
